import { toast } from 'react-toastify';

import { getSerializedErrorMessage } from '../../../../utils/errors';
import {
  applyLoadedEducationNotAttendingSchool,
  finishTreatmentPlanV3Mutation,
  startTreatmentPlanV3Mutation,
} from '../../utils';

import type { ActionReducerMapBuilder } from '@reduxjs/toolkit';
import type { TreatmentPlanV3PlanAction, TreatmentPlanV3State } from '../../types';
import type {
  ITreatmentPlanV3EducationPage,
  UpdateTreatmentPlanV3EducationInfoPayload,
  SetTreatmentPlanV3NotAttendingSchoolPayload,
  TreatmentPlanV3EducationThunks,
  EducationMutationAction,
} from './types';

export function addTreatmentPlanV3EducationCases(
  builder: ActionReducerMapBuilder<TreatmentPlanV3State>,
  thunks: TreatmentPlanV3EducationThunks,
) {
  addGetTreatmentPlanV3EducationPageCases(builder, thunks);
  addUpdateTreatmentPlanV3EducationInfoCases(builder, thunks);
  addSetTreatmentPlanV3NotAttendingSchoolCases(builder, thunks);
}

function isForLoadedEducationPage(state: TreatmentPlanV3State, action: TreatmentPlanV3PlanAction) {
  return action.meta.arg.treatmentPlanId === state.educationPageTreatmentPlanId;
}

function addGetTreatmentPlanV3EducationPageCases(
  builder: ActionReducerMapBuilder<TreatmentPlanV3State>,
  thunks: TreatmentPlanV3EducationThunks,
) {
  const { getTreatmentPlanV3EducationPage } = thunks;

  builder
    .addCase(getTreatmentPlanV3EducationPage.pending, (state, action) => {
      state.educationPage = null;
      state.educationPageLoading = true;
      state.educationPageError = null;
      state.educationPageRequestId = action.meta.requestId;
      state.educationPageTreatmentPlanId = action.meta.arg.treatmentPlanId;
      state.error = null;
    })
    .addCase(getTreatmentPlanV3EducationPage.fulfilled, (state, action) => {
      if (action.meta.requestId !== state.educationPageRequestId) return;
      state.educationPageLoading = false;
      state.educationPageError = null;
      state.educationPage = action.payload;
      applyLoadedEducationNotAttendingSchool(state, action.meta.arg.treatmentPlanId);
    })
    .addCase(getTreatmentPlanV3EducationPage.rejected, (state, action) => {
      if (action.meta.requestId !== state.educationPageRequestId) return;
      state.educationPageLoading = false;
      state.educationPageError = action.error;
      state.error = action.error;
      toast.error(getSerializedErrorMessage(action.error));
    });
}

function addUpdateTreatmentPlanV3EducationInfoCases(
  builder: ActionReducerMapBuilder<TreatmentPlanV3State>,
  thunks: TreatmentPlanV3EducationThunks,
) {
  const { updateTreatmentPlanV3EducationInfo } = thunks;
  const flag = 'updateEducationInfoLoading';

  builder
    .addCase(updateTreatmentPlanV3EducationInfo.pending, (state, action) => {
      startTreatmentPlanV3Mutation(state, flag, action);
    })
    .addCase(
      updateTreatmentPlanV3EducationInfo.fulfilled,
      (
        state,
        action: EducationMutationAction<
          ITreatmentPlanV3EducationPage,
          UpdateTreatmentPlanV3EducationInfoPayload
        >,
      ) => {
        if (!finishTreatmentPlanV3Mutation(state, flag, action)) return;
        if (!isForLoadedEducationPage(state, action)) return;
        state.educationPage = action.payload;
        applyLoadedEducationNotAttendingSchool(state, action.meta.arg.treatmentPlanId);
      },
    )
    .addCase(updateTreatmentPlanV3EducationInfo.rejected, (state, action) => {
      if (!finishTreatmentPlanV3Mutation(state, flag, action)) return;
      toast.error(getSerializedErrorMessage(action.error));
      if (!isForLoadedEducationPage(state, action)) return;
      state.error = action.error;
    });
}

function addSetTreatmentPlanV3NotAttendingSchoolCases(
  builder: ActionReducerMapBuilder<TreatmentPlanV3State>,
  thunks: TreatmentPlanV3EducationThunks,
) {
  const { setTreatmentPlanV3NotAttendingSchool } = thunks;
  const flag = 'setNotAttendingSchoolLoading';

  builder
    .addCase(setTreatmentPlanV3NotAttendingSchool.pending, (state, action) => {
      startTreatmentPlanV3Mutation(state, flag, action);
    })
    .addCase(
      setTreatmentPlanV3NotAttendingSchool.fulfilled,
      (
        state,
        action: EducationMutationAction<
          ITreatmentPlanV3EducationPage,
          SetTreatmentPlanV3NotAttendingSchoolPayload
        >,
      ) => {
        if (!finishTreatmentPlanV3Mutation(state, flag, action)) return;
        if (!isForLoadedEducationPage(state, action)) return;
        state.educationPage = action.payload;
        applyLoadedEducationNotAttendingSchool(state, action.meta.arg.treatmentPlanId);
      },
    )
    .addCase(setTreatmentPlanV3NotAttendingSchool.rejected, (state, action) => {
      if (!finishTreatmentPlanV3Mutation(state, flag, action)) return;
      toast.error(getSerializedErrorMessage(action.error));
      if (!isForLoadedEducationPage(state, action)) return;
      state.error = action.error;
    });
}
